import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import deAuth from './locales/de/auth.json';
import deCommon from './locales/de/common.json';
import deGame from './locales/de/game.json';
import deHome from './locales/de/home.json';
import deLegal from './locales/de/legal.json';
import dePlayer from './locales/de/player.json';
import deSeason from './locales/de/season.json';
import deServer from './locales/de/server.json';
import enAuth from './locales/en/auth.json';
import enCommon from './locales/en/common.json';
import enGame from './locales/en/game.json';
import enHome from './locales/en/home.json';
import enLegal from './locales/en/legal.json';
import enPlayer from './locales/en/player.json';
import enSeason from './locales/en/season.json';
import enServer from './locales/en/server.json';
import esAuth from './locales/es/auth.json';
import esCommon from './locales/es/common.json';
import esGame from './locales/es/game.json';
import esHome from './locales/es/home.json';
import esLegal from './locales/es/legal.json';
import esPlayer from './locales/es/player.json';
import esSeason from './locales/es/season.json';
import esServer from './locales/es/server.json';
import plAuth from './locales/pl/auth.json';
import plCommon from './locales/pl/common.json';
import plGame from './locales/pl/game.json';
import plHome from './locales/pl/home.json';
import plLegal from './locales/pl/legal.json';
import plPlayer from './locales/pl/player.json';
import plSeason from './locales/pl/season.json';
import plServer from './locales/pl/server.json';

/*
 * Ordered as the footer switcher lists them: English first, then the community
 * languages alphabetically by endonym. `SUPPORTED` must stay in step with
 * `SetLocale::SUPPORTED` on the backend - the SPA advertises this tag as
 * `Accept-Language` and expects the strings the API renders back in it.
 *
 * Russian is deliberately absent: none of the display families carry Cyrillic
 * (see `fonts.css` and `scripts/patch-silkscreen-pl.py`), so a `ru` bundle would
 * render its chrome in a fallback family.
 */
export const SUPPORTED_LOCALES = ['en', 'de', 'es', 'pl'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Lifetime of the `ottdpro_lang` cookie copy (one year, refreshed on every visit). */
const LANGUAGE_COOKIE_MINUTES = 60 * 24 * 365;

export const NAMESPACES = [
  'common',
  'auth',
  'home',
  'game',
  'server',
  'season',
  'player',
  'legal',
] as const;

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    home: enHome,
    game: enGame,
    server: enServer,
    season: enSeason,
    player: enPlayer,
    legal: enLegal,
  },
  de: {
    common: deCommon,
    auth: deAuth,
    home: deHome,
    game: deGame,
    server: deServer,
    season: deSeason,
    player: dePlayer,
    legal: deLegal,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    home: esHome,
    game: esGame,
    server: esServer,
    season: esSeason,
    player: esPlayer,
    legal: esLegal,
  },
  pl: {
    common: plCommon,
    auth: plAuth,
    home: plHome,
    game: plGame,
    server: plServer,
    season: plSeason,
    player: plPlayer,
    legal: plLegal,
  },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES,
    defaultNS: 'common',
    ns: NAMESPACES,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'ottdpro-lang',
      // The cookie copy is write-only here: the reverse proxy's static
      // maintenance page (docker/common/nginx/maintenance) reads it to speak
      // the language picked on the site, which localStorage can't tell it.
      caches: ['localStorage', 'cookie'],
      lookupCookie: 'ottdpro_lang',
      cookieMinutes: LANGUAGE_COOKIE_MINUTES,
      cookieOptions: { path: '/', sameSite: 'lax' },
    },
    returnNull: false,
  });

export default i18n;
